import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import { generatePath } from "react-router-dom";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import { BASE_CHART_TIME_RANGES } from "@shared/common/constants";
import {
  AFFILIATION_PATH,
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_1_YEAR,
  TIME_RANGE_3_DAYS,
} from "common/constants";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import { PhoneHeroRanges } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useReferredUsers from "features/affiliation/common/hooks/useReferredUsers";
import { AffiliationTabs } from "features/affiliation/common/types";
import BarsChart, { BarPoint } from "features/affiliation/NewLook/BarsChart";
import { FigureLarge, Meta } from "features/affiliation/NewLook/roles";
import { formatDateLabel } from "@shared/utils/date";

interface ReferredUsersPanelProps {
  /** Links the header to the referred users tab (overview). */
  withSeeAll?: boolean;
  chartHeight?: number;
  className?: string;
}

/**
 * New referred users per period: the count over the range as the headline
 * (it follows the crosshair), quiet bars underneath.
 */
const ReferredUsersPanel = ({
  withSeeAll,
  chartHeight = 160,
  className,
}: ReferredUsersPanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { referredChartQuery, timeRange, setTimeRange } = useReferredUsers(
    AffiliationTabs.REFERRED_USERS,
    TIME_RANGE_1_MONTHS
  );
  const [hovered, setHovered] = useState<BarPoint>();
  const points = useMemo<BarPoint[]>(
    () =>
      (referredChartQuery.data || []).map(([time, value]) => ({
        time: Number(time),
        value: Number(value) || 0,
      })),
    [referredChartQuery.data]
  );
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const range = timeRange.toUpperCase();
  const formats = getTimeFormats(timeRange);
  const loading = referredChartQuery.isFetching && !points.length;
  const { isPhone } = useTheme();
  const changeRange = (value: TimeRange) => {
    setHovered(undefined);
    setTimeRange(value);
  };

  return (
    <Panel
      className={className}
      title={t("overview.new_referred_users")}
      seeAllTo={
        withSeeAll
          ? generatePath(AFFILIATION_PATH, {
              tab: AffiliationTabs.REFERRED_USERS,
            })
          : undefined
      }
      seeAllLabel={t("common.see_all")}
      data-testid="affiliation-referred-users-chart"
    >
      <Header>
        <div>
          <FigureLarge>{hovered ? hovered.value : total}</FigureLarge>
          <Meta>
            {hovered
              ? t("overview.registered_on", {
                  time: formatDateLabel(
                    moment.unix(hovered.time),
                    formats.active
                  ),
                })
              : t("overview.registered_over_range", { range })}
          </Meta>
        </div>
        {!isPhone && (
          <SegmentTabs<TimeRange>
            options={BASE_CHART_TIME_RANGES as TimeRange[]}
            value={timeRange}
            onChange={changeRange}
            ariaLabel={t("overview.referred_range_label")}
          />
        )}
      </Header>
      {loading ? (
        <Skeleton width="100%" height={chartHeight} radius={8} />
      ) : total > 0 ? (
        <BarsChart
          points={points}
          height={chartHeight}
          integer={true}
          formatAxisValue={(value) => value.toLocaleString("en-US")}
          formatTick={(time) =>
            formatDateLabel(moment.unix(time), formats.tick)
          }
          formatActiveTime={(time) =>
            formatDateLabel(moment.unix(time), formats.active)
          }
          onActiveChange={setHovered}
          ariaLabel={t("overview.referred_chart_label", { range })}
        />
      ) : (
        <ChartNote style={{ height: chartHeight }}>
          {t("overview.no_referred_users", { range })}
        </ChartNote>
      )}
      {isPhone && (
        <PhoneHeroRanges<TimeRange>
          options={BASE_CHART_TIME_RANGES as TimeRange[]}
          value={timeRange}
          onChange={changeRange}
          label={t("overview.referred_range_label")}
          surface="panel"
        />
      )}
    </Panel>
  );
};

export default ReferredUsersPanel;

/** Tick and crosshair formats for the range's bucket size. */
const getTimeFormats = (timeRange: TimeRange) => {
  if (timeRange === TIME_RANGE_1_DAY)
    return { tick: "HH:mm", active: "MMM D, HH:mm" };
  if (timeRange === TIME_RANGE_3_DAYS)
    return { tick: "MMM D", active: "MMM D, HH:mm" };
  if (timeRange === TIME_RANGE_1_YEAR)
    return { tick: "MMM", active: "MMM D, YYYY" };
  return { tick: "MMM D", active: "MMM D" };
};

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 16px;
`;
export const ChartNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: center;
`;
